// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a ```mermaid block drawn as a diagram. While a reply streams, the last good diagram stays
// up and newer sources render after a short pause; a failed source only falls back to the code block
// once it has stopped changing, since streams pass through invalid partial states.

import {renderMermaid} from '@app/features/messaging/utils/MermaidRendering';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import {type ReactNode, useEffect, useRef, useState} from 'react';
import {MarkdownBlock, markdownBlockProps} from './MarkdownBlockAttributes';

/** Pause before re-rendering a changed source once a diagram is showing. */
export const MERMAID_RERENDER_DELAY_MS = 200;
/** How long a failed source must stay unchanged before the code block replaces the diagram. */
export const MERMAID_FAILURE_SETTLE_MS = 1000;

interface Drawn {
	source: string;
	dark: boolean;
	svg: string;
}

export function MermaidCodeBlock({
	source,
	fallback,
	children,
}: {
	source: string;
	fallback: ReactNode;
	children: ReactNode;
}) {
	const [dark, setDark] = useState(() => !document.documentElement.classList.contains('theme-light'));
	const [drawn, setDrawn] = useState<Drawn | null>(null);
	const [failedSource, setFailedSource] = useState<string | null>(null);
	const drawnRef = useRef<Drawn | null>(null);
	useEffect(() => {
		const observer = new MutationObserver(() => setDark(!document.documentElement.classList.contains('theme-light')));
		observer.observe(document.documentElement, {attributes: true, attributeFilter: ['class']});
		return () => observer.disconnect();
	}, []);
	useEffect(() => {
		const current = drawnRef.current;
		if (current?.source === source && current.dark === dark) return;
		const changedAt = Date.now();
		let stale = false;
		let failTimer: ReturnType<typeof setTimeout> | undefined;
		const render = () => {
			renderMermaid(source, dark, () => stale)
				.then((svg) => {
					if (stale || svg === null) return;
					drawnRef.current = {source, dark, svg};
					setDrawn(drawnRef.current);
					setFailedSource(null);
				})
				.catch(() => {
					if (stale) return;
					const wait = Math.max(0, MERMAID_FAILURE_SETTLE_MS - (Date.now() - changedAt));
					failTimer = setTimeout(() => setFailedSource(source), wait);
				});
		};
		// Nothing shown yet: render straight away (the code block shows meanwhile).
		const debounce = current ? setTimeout(render, MERMAID_RERENDER_DELAY_MS) : undefined;
		if (!current) render();
		return () => {
			stale = true;
			clearTimeout(debounce);
			clearTimeout(failTimer);
		};
	}, [source, dark]);
	if (!drawn || failedSource === source) return fallback;
	return (
		<div className={markupStyles.codeContainer} {...markdownBlockProps(MarkdownBlock.Code)}>
			{children}
			<div
				style={{overflowX: 'auto', padding: '1rem'}}
				dangerouslySetInnerHTML={{__html: drawn.svg}}
				data-flx="messaging.mermaid.diagram"
			/>
		</div>
	);
}
